import {
  FC,
  useEffect,
  useRef
} from 'react';
import {
  useAsync,
  useCanvasTabs,
  useDesign,
  useServices
} from '@app/hooks';
import { DesignModule, TimeInMs } from '@app/types';
import { wait } from '@app/utilities';
import styles from './page.module.scss';

// Minimum time to prevent the message from flicking.
const minReloadOverlayMs = 0.5 * TimeInMs.second;

type CanvasClientComponent = (
  typeof import('@app/page-components/app/canvas/canvas-client').CanvasClient
);

type CanvasTabHostProps = {
  CanvasClient: CanvasClientComponent;
  isActiveTab: boolean;
  mod: DesignModule;
  /** Whether @see useCanvasTabs flagged this tab's flow as stale. */
  needsRerender: boolean;
};

/**
 * Keeps one module's canvas mounted so it can keep receiving broadcasts, and
 * only shows it while it is the active tab.
 */
const CanvasTabHost: FC<CanvasTabHostProps> = ({
  CanvasClient,
  isActiveTab,
  mod,
  needsRerender
}) => {
  const { loadingService } = useServices();
  const tabRerendered = useCanvasTabs((s) => s.tabRerendered);
  const clientRef = useRef<InstanceType<CanvasClientComponent>>(null);

  useEffect(() => {
    if (isActiveTab) {
      clientRef.current?.updateFocus();
    }
  }, [isActiveTab]);

  useEffect(() => {
    if (!isActiveTab || !needsRerender) {
      return;
    }

    loadingService.useLoading(async () => {
      window.apm.postEventToCurrentView({ target: 'request-reload-data-model' });
      window.apm.postEventToCurrentView({
        commands: { hidePropertyPanel: true },
        target: 'request-update-member'
      });
      tabRerendered(mod.id);
      await wait(minReloadOverlayMs);
    }, 'Reloading Module...');
  }, [
    isActiveTab,
    mod.id,
    needsRerender,
    tabRerendered
  ]);

  return (
    <div
      className="tab"
      style={{ display: (isActiveTab) ? undefined : 'none' }}
    >
      <CanvasClient
        clientId={mod.id}
        node={mod}
        ref={clientRef}
        version={mod.version ?? 1}
      />
    </div>
  );
};

export const AppCanvasPage: FC = () => {
  const serialized = useDesign();
  const activeTabId = useCanvasTabs((s) => s.activeTabId);
  const tabs = useCanvasTabs((s) => s.tabs);
  const tabsToRerender = useCanvasTabs((s) => s.tabsToRerender);
  // Import happens this way to avoid the costly `CustomDiagramWidget` import.
  const [CanvasClient] = useAsync(async () => {
    return (await import('@app/page-components/app/canvas/canvas-client')).CanvasClient;
  }, []);
  const { modules } = serialized.components;
  const moduleMap = new Map(modules.map((mod) => [mod.id, mod]));

  if (!CanvasClient) {
    return null;
  }

  const canvases = tabs.map((tab) => {
    const mod = moduleMap.get(tab.id);

    return (!mod) ? null : (
      <CanvasTabHost
        key={tab.id}
        CanvasClient={CanvasClient}
        isActiveTab={tab.id === activeTabId}
        mod={mod}
        needsRerender={!!tabsToRerender[tab.id]}
      />
    );
  });

  return (
    <div className={styles.canvasTabHosts}>
      {canvases}
    </div>
  );
};
